import {
  Button,
  CardBody,
  ItemCreditCard,
  LoadingSkeleton,
  ModalConfirmation,
} from 'components';
import ButtonReload from 'components/atoms/Button-reload/Button-reload';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { usePaymentMethod } from '../hooks/PaymentMethod.hook';
import './PaymentMethod.css';
import { PaymentMethodForm } from './PaymentMethodForm';

interface PaymentMethodProps {
  onSelectPaymentMethod: (method: string | null) => void;
  /** CB-18: notifica al padre cuando hay 2+ tarjetas y ninguna es la default de Stripe, para
   * bloquear el CTA de "Comprar" hasta que se corrija. */
  onNoDefaultCardChange?: (blocked: boolean) => void;
  /** CB-18: notifica al padre mientras se confirma un cambio de tarjeta predeterminada, para
   * bloquear el CTA de "Comprar" hasta que termine (evita comprar con una selección a medio
   * confirmar). */
  onCardActionPendingChange?: (pending: boolean) => void;
}

/** CB-18: expone `refreshCards` al padre — se usa para refrescar la lista automáticamente al cerrar
 * el modal de error de "Comprar" (mismo patrón ya aplicado en edit-subscription), sin depender de
 * que el usuario recuerde usar el botón de recargar manual. */
export interface PaymentMethodRef {
  refreshCards: () => void;
}

export const PaymentMethod = forwardRef<PaymentMethodRef, PaymentMethodProps>(
  (
    { onSelectPaymentMethod, onNoDefaultCardChange, onCardActionPendingChange },
    ref
  ) => {
    const {
      paymentLink,
      isPaymentLinkModalOpen,
      isGeneratingLink,
      generatePaymentLink,
      handleError,
      handleRefreshCards,
      isLoading,
      error,
      selectedMethod,
      handleMethodChange,
      handleCardSelection,
      creditCards,
      showNewCardForm,
      selectedCard,
      handleShowNewCardForm,
      handleIsPaymentLinkModalOpen,
      willAddCardViaPortal,
      pendingDefaultCard,
      isConfirmingDefault,
      handleConfirmSetDefault,
      handleCancelSetDefault,
      hasNoDefaultCard,
      isOpeningPortal,
      handleAddCardViaPortal,
    } = usePaymentMethod({ onSelectPaymentMethod });

    useImperativeHandle(ref, () => ({ refreshCards: handleRefreshCards }), [
      handleRefreshCards,
    ]);

    useEffect(() => {
      onNoDefaultCardChange?.(hasNoDefaultCard);
    }, [hasNoDefaultCard, onNoDefaultCardChange]);

    useEffect(() => {
      // CB-18: también cuenta `isLoading` (recarga manual, refresh al cerrar el modal de error,
      // refresh tras agregar tarjeta por el form) — sin esto, "Comprar" seguía habilitado durante
      // esos refreshes y podía dispararse con el `selectedPaymentMethod` viejo del padre.
      onCardActionPendingChange?.(isLoading || isConfirmingDefault);
    }, [isLoading, isConfirmingDefault, onCardActionPendingChange]);

    // CB-18: mientras se confirma un cambio de default, la lista sigue mostrando lo que había antes
    // de esa confirmación (potencialmente ya desactualizado) — se trata como loading, igual que un
    // refresh manual, para no dejarla interactiva/engañosa durante esa ventana.
    const isCardListLoading = isLoading || isConfirmingDefault;

    return (
      <CardBody>
        <div className="form__coupon__container">
          <div className="form__coupon__title">
            <h5>MÉTODO DE PAGO</h5>
            {selectedMethod === 'credit_card' && (
              <ButtonReload onClick={handleRefreshCards} />
            )}
          </div>
          <p className="text-yellow-500 mt-2">
            Seleccione el método de pago que desea usar
          </p>
          <div>
            {isCardListLoading ? (
              <div className="loading-spinner">
                <LoadingSkeleton count={2} />
              </div>
            ) : error ? (
              <div className="error-message">{error}</div>
            ) : (
              <div>
                <div className="payment-method-radio-group">
                  <label className="payment-method-radio-item">
                    <input
                      type="radio"
                      value="credit_card"
                      checked={selectedMethod === 'credit_card'}
                      onChange={() => handleMethodChange('credit_card')}
                    />
                    Tarjeta de crédito o débito
                  </label>
                </div>

                {selectedMethod === 'credit_card' && (
                  <div className="payment-method-card-section">
                    {hasNoDefaultCard && (
                      <p className="payment-method-no-default-warning">
                        La tarjeta seleccionada no es la predeterminada en
                        Stripe (o ninguna lo está). Corrige el método
                        predeterminado (ej. desde el botón de abajo) antes de
                        poder comprar.
                      </p>
                    )}

                    {creditCards.length > 0 && !showNewCardForm && (
                      <div className="payment-method-card-list">
                        {creditCards.map((card) => (
                          <ItemCreditCard
                            key={`item__credit__card__${card.id}`}
                            id={card.id}
                            onClickSelect={() => handleCardSelection(card.id)}
                            lastDigits={card.last4}
                            brand={card.brand}
                            isPrincipal={card.is_principal}
                            isSelected={selectedCard === card.id}
                            expMonth={card.exp_month}
                            expYear={card.exp_year}
                          />
                        ))}
                      </div>
                    )}

                    {!showNewCardForm ? (
                      <>
                        {creditCards.length === 0 && (
                          <p>
                            No se encontraron tarjetas de crédito. Por favor,
                            crea una.
                          </p>
                        )}
                        <Button
                          testid="btn_add_credit_card"
                          onClick={() =>
                            willAddCardViaPortal
                              ? handleAddCardViaPortal()
                              : handleShowNewCardForm(!showNewCardForm)
                          }
                          isDisabled={isLoading || isOpeningPortal}
                          isLoading={willAddCardViaPortal && isOpeningPortal}
                          typeStyle={'rounded'}
                          template={'primary'}
                          color={'primary'}
                          size={'small'}
                        >
                          Agregar tarjeta
                        </Button>
                      </>
                    ) : (
                      <>
                        <PaymentMethodForm
                          isPaymentMethodLink={false}
                          handleRefreshCards={handleRefreshCards}
                          handleShowNewCardForm={handleShowNewCardForm}
                          handleError={handleError}
                          isGeneratingLink={isGeneratingLink}
                          generatePaymentLink={generatePaymentLink}
                        />
                      </>
                    )}

                    {willAddCardViaPortal && !showNewCardForm && (
                      <button
                        type="button"
                        className="payment-method-link"
                        onClick={generatePaymentLink}
                      >
                        {isGeneratingLink
                          ? 'Generando link...'
                          : 'Generar link a cliente'}
                      </button>
                    )}
                  </div>
                )}
                <label className="payment-method-radio-item payment-method-radio-item-disabled">
                  <input
                    type="radio"
                    value="ath_movil"
                    checked={selectedMethod === 'ath_movil'}
                    onChange={() => handleMethodChange('ath_movil')}
                    disabled
                  />
                  ATH Móvil (Próximamente)
                </label>
              </div>
            )}
          </div>

          {isPaymentLinkModalOpen && (
            <div className="payment-method-modal">
              <div className="payment-method-modal-content">
                <div className="payment-method-modal-header">
                  <h3 className="payment-method-modal-title">
                    Link para Agregar Método de Pago
                  </h3>
                  <p className="payment-method-modal-description">
                    Utiliza el siguiente enlace para agregar tu método de pago
                    de forma segura:
                  </p>
                </div>
                <div className="payment-method-modal-link">
                  <a
                    href={paymentLink || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {paymentLink}
                  </a>
                </div>
                <p>
                  {willAddCardViaPortal
                    ? 'Este enlace expira en 5 minutos si nadie lo abre — si se vence, genera uno nuevo con este mismo botón.'
                    : 'Este enlace es válido por 24 horas y solo puede ser utilizado una vez.'}
                </p>
                <div className="payment-method-modal-footer">
                  <Button
                    onClick={() => handleIsPaymentLinkModalOpen(false)}
                    isDisabled={isGeneratingLink}
                    typeStyle={'rounded'}
                    template={'primary'}
                    color={'primary'}
                    size={'small'}
                    label="Cerrar"
                  />
                </div>
              </div>
            </div>
          )}

          <ModalConfirmation
            isOpen={Boolean(pendingDefaultCard)}
            onClose={handleCancelSetDefault}
            onConfirm={handleConfirmSetDefault}
            isLoading={isConfirmingDefault}
            title="Cambiar tarjeta predeterminada"
            message={`Vas a comprar con la tarjeta terminada en ${
              pendingDefaultCard?.last4 ?? ''
            }, que no es la predeterminada en Stripe. Al continuar, pasará a ser la predeterminada y será la que se use en próximas renovaciones. ¿Confirmas?`}
            confirmText="Sí, continuar"
            cancelText="Cancelar"
          />
        </div>
      </CardBody>
    );
  }
);

PaymentMethod.displayName = 'PaymentMethod';
